import { useCallback } from 'react';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { logger } from '@/lib/logger';
import { useHealth } from '~/health/contexts/health/Health';
import { useModal } from '@/contexts/ModalContext';
import { SessionMatcher } from '~/health/lib/session-matcher/session-matcher';
import { RecordedSessionMapper } from '~/health/lib/recorded-session/recorded-session-mapper';
import type { RecordedSessionSample } from '~/health/lib/health-provider/health-provider';
import { RecordedSessionMatchSheet } from '~/health/components/recorded-session-match-sheet/RecordedSessionMatchSheet';

/**
 * Widen the health-store read window past the exact workout bounds so a session
 * that started a little before / ended a little after the logged workout is
 * still returned; the matcher then requires real overlap with the precise
 * `[startedAt, finishedAt]` window.
 */
const READ_WINDOW_BUFFER_MS = 30 * 60 * 1000;

const matcher = new SessionMatcher();

/**
 * The #29 auto-match: once, when a workout finishes, read the health store for
 * sessions overlapping the workout, and — if any are found and the user has
 * connected — let them confirm/pick which to attach. Runs only where a health
 * store exists and is connected; a no-match or an unavailable store is a silent
 * no-op (the workout is left as-is). Re-checks / manual link are #31.
 */
export const useRecordedSessionImport = () => {
  // usePowerSync()'s kysely type resolves to `never` codebase-wide (see
  // useWorkoutActions); the runtime value is the real query builder.
  const powerSync = usePowerSync();
  const { isAvailable, isConnected, readWorkoutSessions } = useHealth();
  const { showModal } = useModal();

  const attach = useCallback(
    async (
      workoutId: string,
      userId: string,
      sample: RecordedSessionSample,
    ) => {
      // Replace any prior session so a re-run never double-attaches.
      await db.RecordedSession.mutations.softDeleteByWorkout(
        powerSync,
        workoutId,
      );
      await db.RecordedSession.mutations.create(
        powerSync,
        RecordedSessionMapper.toCreateInput(sample, { workoutId, userId }),
      );
    },
    [powerSync],
  );

  const importForWorkout = useCallback(
    async (workoutId: string) => {
      if (!isAvailable || !isConnected) return;

      try {
        const workout = await powerSync
          .selectFrom('Workout')
          .select(['id', 'userId', 'startedAt', 'finishedAt'])
          .where('id', '=', workoutId)
          .executeTakeFirst();

        if (!workout?.startedAt || !workout.finishedAt || !workout.userId) {
          return;
        }

        const startedAt = new Date(workout.startedAt);
        const finishedAt = new Date(workout.finishedAt);
        const userId = workout.userId;

        const sessions = await readWorkoutSessions({
          from: new Date(startedAt.getTime() - READ_WINDOW_BUFFER_MS),
          to: new Date(finishedAt.getTime() + READ_WINDOW_BUFFER_MS),
        });

        const result = matcher.match({ startedAt, finishedAt }, sessions);
        if (result.status === 'none') return;

        showModal(RecordedSessionMatchSheet, {
          candidates: result.candidates,
          onAttach: (sample: RecordedSessionSample) =>
            attach(workoutId, userId, sample),
        });
      } catch (error) {
        logger.error(error, '[Health] recorded-session import failed');
      }
    },
    [
      isAvailable,
      isConnected,
      readWorkoutSessions,
      powerSync,
      showModal,
      attach,
    ],
  );

  return { importForWorkout };
};
